import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  PhoneAndroid as PhoneIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../../components/Layout';
import invoiceService from '../../services/invoiceService';
import paymentService from '../../services/paymentService';
import penaltyService from '../../services/penaltyService';

const TenantInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'mpesa',
    transaction_ref: '',
  });
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [minPartialAmount, setMinPartialAmount] = useState(0);
  // STK Push state
  const [mpesaMode, setMpesaMode] = useState('stk_push'); // 'stk_push' or 'manual'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [stkLoading, setStkLoading] = useState(false);
  const [stkStatus, setStkStatus] = useState(null); // null | 'sent' | 'success' | 'failed' | 'timeout'
  const [checkoutRequestId, setCheckoutRequestId] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollCountRef = useRef(0);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const [invoicesRes, penaltiesRes] = await Promise.all([
        invoiceService.getMyInvoices(),
        penaltyService.getMyPenalties().catch(() => ({ data: { penalties: [] } }))
      ]);
      
      const tenantInvoices = invoicesRes.data || [];
      const penaltiesData = penaltiesRes.data?.penalties || [];
      
      // Create a map of invoice_id to total penalty amount
      const penaltyMap = {};
      penaltiesData.forEach(penalty => {
        const invoiceId = penalty.invoice_id;
        if (!penaltyMap[invoiceId]) {
          penaltyMap[invoiceId] = 0;
        }
        penaltyMap[invoiceId] += parseFloat(penalty.penalty_amount || 0);
      });
      
      // Merge penalty data into invoices
      const invoicesWithPenalties = tenantInvoices.map(invoice => ({
        ...invoice,
        penalty_amount: penaltyMap[invoice.id] || 0,
        total_with_penalty: (parseFloat(invoice.amount) || 0) + (penaltyMap[invoice.id] || 0)
      }));
      
      setInvoices(invoicesWithPenalties);
    } catch (error) {
      console.log('Error loading invoices:', error);
      setInvoices([]);
      if (error.response?.status !== 404) {
        console.error('Failed to load invoices:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    // Calculate correct remaining balance including penalty
    const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
    const remainingBalance = totalAmount - (invoice.amount_paid || 0);
    const minPartial = invoice.amount * 0.5;
    setMinPartialAmount(minPartial);
    setPaymentData({
      amount: remainingBalance, // Default to remaining balance
      payment_method: 'mpesa',
      transaction_ref: '',
    });
    setIsPartialPayment(false);
    setOpenPaymentDialog(true);
  };

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  const handleClosePaymentDialog = () => {
    stopPolling();
    setOpenPaymentDialog(false);
    setSelectedInvoice(null);
    setStkStatus(null);
    setStkLoading(false);
    setCheckoutRequestId(null);
    setPhoneNumber('');
    setMpesaMode('stk_push');
  };

  // Poll STK Push status
  const startPolling = useCallback((reqId) => {
    pollCountRef.current = 0;
    pollIntervalRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      // Timeout after 12 polls (60 seconds)
      if (pollCountRef.current > 12) {
        stopPolling();
        setStkStatus('timeout');
        setStkLoading(false);
        return;
      }
      try {
        const res = await paymentService.queryMpesaStatus(reqId);
        const data = res.data;
        if (data.ResultCode === '0' || data.ResultCode === 0) {
          // Payment successful
          stopPolling();
          setStkStatus('success');
          setStkLoading(false);
          toast.success('Payment received successfully! Invoice updated.');
          setTimeout(() => {
            handleClosePaymentDialog();
            fetchInvoices();
          }, 2000);
        } else if (data.ResultCode && data.ResultCode !== '0') {
          // Payment failed or cancelled
          stopPolling();
          setStkStatus('failed');
          setStkLoading(false);
          toast.error(data.ResultDesc || 'Payment was not completed. Please try again.');
        }
      } catch (err) {
        // Query may fail if still pending — keep polling
        console.log('STK query pending...', err?.response?.data);
      }
    }, 5000);
  }, [stopPolling]);

  // Handle STK Push initiation
  const handleStkPush = async () => {
    // Validate phone number (must be 254...)
    let phone = phoneNumber.trim();
    if (phone.startsWith('0')) {
      phone = '254' + phone.substring(1);
    } else if (phone.startsWith('+254')) {
      phone = phone.substring(1);
    } else if (!phone.startsWith('254')) {
      toast.error('Enter a valid phone number (e.g. 0712345678 or 254712345678)');
      return;
    }

    if (phone.length !== 12) {
      toast.error('Phone number must be 12 digits (e.g. 254712345678)');
      return;
    }

    setStkLoading(true);
    setStkStatus(null);
    try {
      const amount = parseInt(paymentData.amount);
      const res = await paymentService.initiateMpesaPayment(
        selectedInvoice.id, phone, amount
      );
      const data = res.data;

      if (data.ResponseCode === '0') {
        setStkStatus('sent');
        setCheckoutRequestId(data.CheckoutRequestID);
        toast.info('Payment request sent to your phone. Please enter your M-Pesa PIN.', { autoClose: 10000 });
        startPolling(data.CheckoutRequestID);
      } else {
        setStkLoading(false);
        setStkStatus('failed');
        toast.error(data.errorMessage || data.ResponseDescription || 'Failed to send payment request. Try again.');
      }
    } catch (error) {
      setStkLoading(false);
      setStkStatus('failed');
      toast.error(error.response?.data?.detail || 'Failed to initiate payment. Check your phone number and try again.');
    }
  };

  // Handle manual payment submission (Paybill / Bank / Cash)
  const handlePaymentSubmit = async () => {
    try {
      const response = await paymentService.submitManualPayment({
        invoice_id: selectedInvoice.id,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        transaction_reference: paymentData.transaction_ref
      });
      
      const data = response.data;
      
      if (data.is_partial_payment && data.grace_period_message) {
        toast.success(
          `Partial payment recorded! ${data.grace_period_message}`,
          { autoClose: 8000 }
        );
      } else if (data.invoice_status === 'paid') {
        toast.success('Payment completed! Invoice fully paid.');
      } else {
        toast.success('Payment recorded successfully!');
      }
      
      if (data.penalty_applied > 0) {
        toast.warning(
          `Late payment penalty of KES ${data.penalty_applied.toLocaleString()} has been applied.`,
          { autoClose: 6000 }
        );
      }
      
      handleClosePaymentDialog();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partial_paid':
        return 'info';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const unpaidInvoices = invoices.filter(i => i.status !== 'paid');
  const totalDue = unpaidInvoices.reduce((sum, i) => sum + (i.remaining_balance || i.amount || 0), 0);

  if (loading) {
    return <Loading message="Loading your invoices..." />;
  }

  return (
    <Box>
      <PageHeader
        title="My Invoices"
        subtitle="View and pay your rent invoices"
        breadcrumbs={[{ label: 'Invoices' }]}
      />

      {totalDue > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have {unpaidInvoices.length} unpaid invoice(s) totaling KES {totalDue.toLocaleString()}.
          Please make payment to avoid penalties.
        </Alert>
      )}

      {invoices.length === 0 ? (
        <EmptyState
          title="No Active Invoices"
          description="You don't have any invoices to pay at the moment. Invoices will appear here once your landlord generates them."
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Invoice #</strong></TableCell>
                <TableCell><strong>Month</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Paid (KES)</strong></TableCell>
                <TableCell><strong>Balance (KES)</strong></TableCell>
                <TableCell><strong>Penalty (KES)</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => {
                return (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoice_number}</TableCell>
                    <TableCell>
                      {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long' 
                      }) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {invoice.penalty_amount > 0 ? (
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {invoice.total_with_penalty?.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({invoice.amount?.toLocaleString()} + {invoice.penalty_amount?.toLocaleString()})
                          </Typography>
                        </Box>
                      ) : (
                        invoice.amount?.toLocaleString()
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          color={(invoice.amount_paid || 0) > 0 ? 'success.main' : 'text.secondary'}
                          fontWeight={(invoice.amount_paid || 0) > 0 ? 600 : 400}
                        >
                          {(invoice.amount_paid || 0).toLocaleString()}
                        </Typography>
                        {invoice.has_credit_payment && invoice.credit_applied > 0 && (
                          <Typography variant="caption" color="info.main" fontWeight="bold">
                            (Credit: {invoice.credit_applied?.toLocaleString()})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
                        const balance = totalAmount - (invoice.amount_paid || 0);
                        
                        // If balance is negative, it's an overpayment (credit)
                        if (balance < 0) {
                          const creditAmount = Math.abs(balance);
                          return (
                            <Box>
                              <Typography variant="body2" fontWeight="600" color="success.main">
                                0
                              </Typography>
                              <Typography variant="caption" color="info.main" fontWeight="bold">
                                Credit: {creditAmount.toLocaleString()}
                              </Typography>
                            </Box>
                          );
                        }
                        
                        // Normal balance display
                        return (
                          <Typography
                            variant="body2"
                            color={balance > 0 ? 'warning.main' : 'success.main'}
                            fontWeight={600}
                          >
                            {balance.toLocaleString()}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {invoice.penalty_amount > 0 ? (
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          {invoice.penalty_amount?.toLocaleString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(invoice.due_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
                        const balance = totalAmount - (invoice.amount_paid || 0);
                        const isOverpaid = balance < 0;
                        let actualStatus = invoice.status;
                        
                        // Recalculate status based on actual balance including penalties
                        if (balance <= 0) {
                          actualStatus = 'paid';
                        } else if (invoice.amount_paid > 0 && balance > 0) {
                          actualStatus = 'partial_paid';
                        }
                        
                        return (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip
                              label={actualStatus?.toUpperCase().replace('_', ' ')}
                              color={getStatusColor(actualStatus)}
                              size="small"
                            />
                            {isOverpaid && (
                              <Chip
                                label="Overpaid"
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
                        const balance = totalAmount - (invoice.amount_paid || 0);
                        
                        // If overpaid, show different label
                        if (balance < 0) {
                          return (
                            <Chip label="Fully Paid" color="success" size="small" variant="outlined" />
                          );
                        }
                        
                        // If has balance, show payment button
                        if (balance > 0) {
                          return (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<PaymentIcon />}
                              onClick={() => handleOpenPaymentDialog(invoice)}
                            >
                              {balance < invoice.amount ? 'Pay Balance' : 'Pay Now'}
                            </Button>
                          );
                        }
                        
                        // Fully paid
                        return <Chip label="Fully Paid" color="success" size="small" />;
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Make Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Invoice: {selectedInvoice?.invoice_number}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Due Date: {selectedInvoice && new Date(selectedInvoice.due_date).toLocaleDateString()}
          </Typography>
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Original Invoice Amount
            </Typography>
            <Typography variant="h6" color="primary">
              KES {selectedInvoice?.amount?.toLocaleString()}
            </Typography>
            
            {selectedInvoice?.penalty_amount > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Late Payment Penalty
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  KES {selectedInvoice?.penalty_amount?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total Amount (with penalty)
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  KES {selectedInvoice?.total_with_penalty?.toLocaleString()}
                </Typography>
              </>
            )}
            
            {selectedInvoice?.amount_paid > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Amount Paid
                </Typography>
                <Typography variant="h6" color="success.main">
                  KES {selectedInvoice?.amount_paid?.toLocaleString()}
                </Typography>
              </>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Remaining Balance
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="warning.main">
              KES {(() => {
                if (!selectedInvoice) return '0';
                const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
                const balance = totalAmount - (selectedInvoice.amount_paid || 0);
                return balance.toLocaleString();
              })()}
            </Typography>
          </Box>
          
          {(!selectedInvoice?.amount_paid || selectedInvoice?.amount_paid === 0) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Partial Payment Option:</strong> You can pay at least 50% now (KES {minPartialAmount.toLocaleString()}) and the rest within 10 days without penalties.
              </Typography>
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Payment Amount (KES)"
            name="amount"
            type="number"
            value={paymentData.amount}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              const remaining = selectedInvoice?.remaining_balance || selectedInvoice?.amount;
              const isPartial = value < remaining;
              setIsPartialPayment(isPartial);
              setPaymentData({ ...paymentData, amount: e.target.value });
            }}
            margin="normal"
            required
            helperText={selectedInvoice?.amount_paid > 0 
              ? `Remaining balance: KES ${(selectedInvoice?.remaining_balance || 0).toLocaleString()}`
              : `Minimum partial payment: KES ${minPartialAmount.toLocaleString()}`
            }
            inputProps={{
              min: selectedInvoice?.amount_paid > 0 ? 100 : minPartialAmount,
              max: selectedInvoice?.remaining_balance || selectedInvoice?.amount,
              step: 100
            }}
          />
          
          {isPartialPayment && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              <Typography variant="body2">
                ⚠️ <strong>Partial Payment:</strong> You'll have 10 days from today to pay the remaining balance without additional penalties.
              </Typography>
            </Alert>
          )}
          
          <TextField
            fullWidth
            select
            label="Payment Method"
            name="payment_method"
            value={paymentData.payment_method}
            onChange={(e) => {
              setPaymentData({ ...paymentData, payment_method: e.target.value, transaction_ref: '' });
              setStkStatus(null);
              setStkLoading(false);
              stopPolling();
            }}
            margin="normal"
            required
          >
            <MenuItem value="mpesa">M-Pesa</MenuItem>
            <MenuItem value="bank">Bank Transfer</MenuItem>
            <MenuItem value="cash">Cash</MenuItem>
          </TextField>

          {paymentData.payment_method === 'mpesa' && (
            <Box sx={{ mt: 2 }}>
              {/* Only STK Push is available for tenants. Manual paybill validation is handled by landlord. */}

              {(() => {
                // Force STK Push mode for tenants
                if (mpesaMode !== 'stk_push') setMpesaMode('stk_push');
                return null;
              })()}

              <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Enter your M-Pesa phone number below. You will receive a payment prompt on your phone.
                    </Typography>
                  </Alert>
                  <TextField
                    fullWidth
                    label="M-Pesa Phone Number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="0712345678 or 254712345678"
                    margin="normal"
                    required
                    disabled={stkLoading}
                    helperText="Enter the phone number registered with M-Pesa"
                  />

                  {stkStatus === 'sent' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">
                          <strong>Waiting for payment...</strong> Check your phone and enter your M-Pesa PIN to complete.
                        </Typography>
                      </Box>
                    </Alert>
                  )}

                  {stkStatus === 'success' && (
                    <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                      <Typography variant="body2"><strong>Payment received!</strong> Invoice has been updated.</Typography>
                    </Alert>
                  )}

                  {stkStatus === 'failed' && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="body2">Payment was not completed. Please try again.</Typography>
                    </Alert>
                  )}

                  {stkStatus === 'timeout' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        Payment confirmation timed out. If you completed the payment, it will reflect shortly.
                        Otherwise, please try again.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}

          {paymentData.payment_method === 'bank' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Bank:</strong> Example Bank<br />
                <strong>Account:</strong> 1234567890<br />
                <strong>Account Name:</strong> Property Management<br />
                Enter transaction reference below after payment.
              </Typography>
            </Alert>
          )}

          {(paymentData.payment_method === 'bank' || paymentData.payment_method === 'cash') && (
            <TextField
              fullWidth
              label="Transaction Reference"
              name="transaction_ref"
              value={paymentData.transaction_ref}
              onChange={(e) => setPaymentData({ ...paymentData, transaction_ref: e.target.value })}
              margin="normal"
              required
              placeholder={paymentData.payment_method === 'bank' ? 'Enter bank reference' : 'Enter receipt number'}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog} disabled={stkLoading && stkStatus === 'sent'}>Cancel</Button>
          {paymentData.payment_method === 'mpesa' && mpesaMode === 'stk_push' ? (
            <Button
              onClick={handleStkPush}
              variant="contained"
              color="success"
              disabled={!phoneNumber.trim() || stkLoading || stkStatus === 'success'}
              startIcon={stkLoading ? <CircularProgress size={18} color="inherit" /> : <PhoneIcon />}
            >
              {stkLoading ? 'Waiting...' : stkStatus === 'failed' || stkStatus === 'timeout' ? 'Retry Payment' : 'Send to Phone'}
            </Button>
          ) : (
            <Button
              onClick={handlePaymentSubmit}
              variant="contained"
              disabled={!paymentData.transaction_ref}
            >
              Submit Payment
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantInvoices;
